'use client';

import { useEffect, useState } from 'react';
import { Search, MessageSquare, Send, Loader2, ChevronLeft, ChevronRight, Reply } from 'lucide-react';
import { useNumbers } from '@/hooks/useNumbers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime, cn } from '@/lib/utils';

export function SMSPage() {
  const { assignments, messages, loading, fetchAssignments, fetchMessages, sendSms } = useNumbers();
  const [selectedAssignment, setSelectedAssignment] = useState<string>('');
  const [search, setSearch] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [toNumber, setToNumber] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const messagesPerPage = 20;

  useEffect(() => {
    fetchAssignments();
    if (selectedAssignment) {
      fetchMessages({ assignmentId: selectedAssignment, limit: messagesPerPage, page: currentPage });
    }
  }, [fetchAssignments, fetchMessages, selectedAssignment, currentPage]);

  useEffect(() => {
    if (assignments.length > 0 && !selectedAssignment) {
      setSelectedAssignment(assignments[0].id);
    }
  }, [assignments, selectedAssignment]);

  const filteredMessages = messages.filter(m => {
    if (search && !m.body.toLowerCase().includes(search.toLowerCase()) &&
        !m.fromNumber.includes(search) &&
        !m.toNumber.includes(search)) return false;
    if (directionFilter && m.direction !== directionFilter) return false;
    if (statusFilter && m.status !== statusFilter) return false;
    return true;
  });

  const handleSend = async () => {
    if (!toNumber || !messageBody || !selectedAssignment) return;
    setSending(true);
    const assignment = assignments.find(a => a.id === selectedAssignment);
    if (assignment) {
      await sendSms({
        to: toNumber,
        from: assignment.phoneNumber,
        body: messageBody,
      });
      setToNumber('');
      setMessageBody('');
      setSendDialogOpen(false);
    }
    setSending(false);
  };

  const getDirectionIcon = (direction: string) => (
    direction === 'inbound' ? (
      <MessageSquare className="h-4 w-4 text-green-400" />
    ) : (
      <Send className="h-4 w-4 text-blue-400" />
    )
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'received':
        return <Badge className="bg-green-500/10 text-green-400">Received</Badge>;
      case 'sent':
        return <Badge className="bg-blue-500/10 text-blue-400">Sent</Badge>;
      case 'delivered':
        return <Badge className="bg-purple-500/10 text-purple-400">Delivered</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/10 text-red-400">Failed</Badge>;
      case 'queued':
        return <Badge className="bg-amber-500/10 text-amber-400">Queued</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="glass">
          <CardContent className="pt-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">SMS Inbox</h1>
          <p className="text-slate-400 mt-1">View and send SMS messages</p>
        </div>
        <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Send className="mr-2 h-4 w-4" />
              Compose
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Send SMS</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">To</label>
                <Input
                  placeholder="+15551234567"
                  value={toNumber}
                  onChange={e => setToNumber(e.target.value)}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Message</label>
                <Textarea
                  placeholder="Type your message..."
                  value={messageBody}
                  onChange={e => setMessageBody(e.target.value)}
                  rows={4}
                />
                <p className="text-xs text-slate-500">
                  {messageBody.length}/160 characters ({Math.ceil(messageBody.length / 160)} segments)
                </p>
              </div>
              {selectedAssignment && (
                <p className="text-xs text-slate-500">
                  Sending from {assignments.find(a => a.id === selectedAssignment)?.phoneNumber}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSend} disabled={sending || !toNumber || !messageBody}>
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Send'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <Card className="glass lg:col-span-1">
          <CardHeader>
            <CardTitle>Numbers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {assignments.map(assignment => (
                <Button
                  key={assignment.id}
                  variant={selectedAssignment === assignment.id ? 'default' : 'outline'}
                  className="w-full justify-start gap-3"
                  onClick={() => { setSelectedAssignment(assignment.id); setCurrentPage(1); }}
                >
                  <MessageSquare className={cn('h-4 w-4', selectedAssignment === assignment.id ? 'text-white' : 'text-slate-400')} />
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm">{assignment.phoneNumber}</p>
                    <p className="text-xs text-slate-400">{assignment.status}</p>
                  </div>
                </Button>
              ))}
              {assignments.length === 0 && (
                <p className="text-center text-slate-500 py-4">No numbers assigned</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="glass lg:col-span-3">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Messages</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    placeholder="Search messages..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-10 w-48"
                  />
                </div>
                <Select value={directionFilter} onValueChange={(v) => setDirectionFilter(v)}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                    <SelectItem value="outbound">Outbound</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All</SelectItem>
                    <SelectItem value="received">Received</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {filteredMessages.length > 0 ? (
                filteredMessages.map(message => (
                  <div
                    key={message.id}
                    className={cn(
                      'p-4 rounded-xl border transition-all',
                      message.direction === 'inbound'
                        ? 'bg-green-500/5 border-green-500/10'
                        : 'bg-blue-500/5 border-blue-500/10'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn('p-2 rounded-lg', message.direction === 'inbound' ? 'bg-green-500/10' : 'bg-blue-500/10')}>
                        {getDirectionIcon(message.direction)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <p className="font-mono font-medium text-white">
                              {message.direction === 'inbound' ? message.fromNumber : message.toNumber}
                            </p>
                            {getStatusBadge(message.status)}
                          </div>
                          <p className="text-xs text-slate-500 whitespace-nowrap">
                            {formatDateTime(message.createdAt)}
                          </p>
                        </div>
                        <p className="text-slate-300 whitespace-pre-wrap">{message.body}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span>{message.segments} segment(s)</span>
                          <span>{message.encoding}</span>
                          {message.rewardAmount > 0 && (
                            <span className="text-green-400">+${message.rewardAmount.toFixed(4)}</span>
                          )}
                        </div>
                      </div>
                      {message.direction === 'inbound' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-cyan-400"
                          onClick={() => { setToNumber(message.fromNumber); setSendDialogOpen(true); }}
                        >
                          <Reply className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                  <p>No messages found</p>
                  {selectedAssignment && (
                    <Button variant="outline" className="mt-4" onClick={() => setSendDialogOpen(true)}>
                      <Send className="mr-2 h-4 w-4" />
                      Send First Message
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
              <p className="text-sm text-slate-400">
                Showing {filteredMessages.length} messages
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-slate-300">Page {currentPage}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={filteredMessages.length < messagesPerPage}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}